// frontend/src/components/GuardLayout.tsx
import { useState, useEffect, useMemo } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import guardAuthService from '../services/guardAuth.service';
import yardService from '../services/yard.service';
import { YardLocation } from '../types/yard';

export default function GuardLayout() {
  // The /yard tree renders outside the employee shell, so it needs its own title
  // updater — App.tsx only stamps a title once at mount, which left every yard page
  // after the first showing whatever the previous one set.
  usePageTitle();
  const navigate = useNavigate();
  const location = useLocation();
  // Memoized ON PURPOSE: getUser() JSON-parses localStorage on every call, so
  // an un-memoized read minted a NEW managed_yard_ids array each render. The
  // fetch effect below depends on that array, and its own setYards() re-render
  // then re-fired it — an unbounded /yard/locations loop (~400 req/min from
  // one kiosk, 68k requests in two hours, prod 2026-08-26). The user can't
  // change while this layout is mounted: login/logout hard-navigate.
  const user = useMemo(() => guardAuthService.getUser(), []);
  const [selectedYard, setSelectedYard] = useState<number | null>(
    user?.default_location_id || null
  );
  const [yards, setYards] = useState<YardLocation[]>([]);

  useEffect(() => {
    if (!guardAuthService.isAuthenticated()) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (!guardAuthService.isAuthenticated()) return;
    yardService.getYardLocations().then((all) => {
      // Lock the dropdown to the user's managed yards (if any). An empty
      // managed list means "no restriction" and we show every yard.
      const managed = user?.managed_yard_ids;
      const visible = managed && managed.length > 0
        ? all.filter((y) => managed.includes(y.recno))
        : all;
      setYards(visible);

      // If the user's default_location_id isn't in their managed yards,
      // fall back to the first allowed yard so the dropdown isn't blank.
      setSelectedYard((current) => {
        if (current && visible.some((y) => y.recno === current)) return current;
        return visible[0]?.recno ?? null;
      });
    }).catch(console.error);
  }, [user?.managed_yard_ids]);

  const handleLogout = () => {
    guardAuthService.logout();
    // Hard-navigate so the guard route tree unregisters cleanly before any
    // subsequent render tries to resolve /yard/* against it.
    window.location.assign('/login');
  };

  const navItems = [
    { name: 'Reefer Log', href: '/yard/reefer-log' },
    { name: 'Update Yard Inventory', href: '/yard/inventory' },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Top navbar */}
      <nav className="flex-shrink-0 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <span className="font-bold text-lg">GWI 360</span>
              <select
                value={selectedYard || ''}
                onChange={(e) => setSelectedYard(e.target.value ? Number(e.target.value) : null)}
                className="bg-blue-800 text-white rounded px-2 py-1 text-sm"
              >
                <option value="">Select Yard</option>
                {yards.map((yard) => (
                  <option key={yard.recno} value={yard.recno}>
                    {yard.locname}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm">{user?.display_name || user?.username}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Navigation tabs */}
      <div className="flex-shrink-0 bg-panel shadow">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-3 font-medium whitespace-nowrap text-sm ${
                  isActive(item.href)
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main content - scrollable */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Outlet context={{ selectedYard, setSelectedYard }} />
        </div>
      </main>
    </div>
  );
}
